/* CappyRoots direct-feed Yotpo reviews. Public CDN only; no private credentials or Shopify writes. */
(() => {
  'use strict';

  if (window.CappyRootsYotpoReviews) {
    window.CappyRootsYotpoReviews.initAll(document);
    return;
  }

  const STORE_ID = 'YlYVZ628L8o8sZTz7qlISH5JbJCGVXd1j2Bw2KJm';
  const PUBLIC_ENDPOINT = `https://api-cdn.yotpo.com/v1/widget/${encodeURIComponent(STORE_ID)}/products/`;
  const MAX_PER_PAGE = 150;
  const REQUEST_SIZE = 50;
  const MAX_PAGES_PER_PRODUCT = 8;
  const VISIBLE_PAGE_SIZE = 10;
  const feedCache = new Map();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = String(text);
    return node;
  };

  const setHidden = (node, hidden) => {
    if (node) node.hidden = Boolean(hidden);
  };

  const revealReviewNavigation = () => {
    document.querySelectorAll('[data-cr-review-nav]').forEach((node) => { node.hidden = false; });
  };

  const safeText = (value) => (value === null || value === undefined ? '' : String(value));

  const safeUrl = (value) => {
    if (!value || typeof value !== 'string') return '';
    try {
      const url = new URL(value, window.location.origin);
      return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : '';
    } catch (error) {
      return '';
    }
  };

  const numeric = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const averageLabel = (score) => numeric(score).toFixed(1);
  const plural = (count, single, many) => `${count} ${count === 1 ? single : many}`;

  const endpointFor = (productId, page, perPage) => {
    const boundedPerPage = Math.max(1, Math.min(MAX_PER_PAGE, numeric(perPage, REQUEST_SIZE)));
    const boundedPage = Math.max(1, numeric(page, 1));
    return `${PUBLIC_ENDPOINT}${encodeURIComponent(String(productId))}/reviews.json?per_page=${boundedPerPage}&page=${boundedPage}`;
  };

  const requestPage = async (productId, page, perPage = REQUEST_SIZE) => {
    const url = endpointFor(productId, page, perPage);
    const response = await fetch(url, { method: 'GET', mode: 'cors', credentials: 'omit', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`The reviews service returned ${response.status}.`);
    const body = await response.json();
    if (body?.status?.code && Number(body.status.code) >= 400) throw new Error('The reviews service could not return published reviews.');
    const payload = body?.response;
    if (!payload || !Array.isArray(payload.reviews)) throw new Error('The reviews service returned an unexpected response.');
    return payload;
  };

  const isVerified = (review) => Boolean(
    review?.verified_buyer || review?.verified || review?.is_verified_buyer || review?.user?.verified_buyer
  );

  const isIncentivized = (review) => Boolean(
    review?.incentivized_review || review?.incentivized || review?.is_incentivized || review?.is_incentivized_review
  );

  const imageUrls = (review) => {
    const images = Array.isArray(review?.images_data) ? review.images_data : [];
    return images.map((image) => {
      if (typeof image === 'string') return safeUrl(image);
      if (!image || typeof image !== 'object') return '';
      return safeUrl(image.original_url || image.url || image.full_url || image.big || image.thumb_url || image.thumbnail_url);
    }).filter(Boolean);
  };

  const merchantReply = (review) => {
    const candidate = review?.merchant_response || review?.merchant_reply || review?.reply || review?.response;
    if (typeof candidate === 'string') return candidate.trim();
    if (candidate && typeof candidate === 'object') return safeText(candidate.content || candidate.body || candidate.text || candidate.comment).trim();
    return '';
  };

  const reviewerName = (review) => safeText(
    review?.user?.display_name || review?.user?.name || review?.display_name || review?.reviewer_name
  ).trim();

  const rawReviewDate = (review) => review?.created_at || review?.created_at_date || review?.date || review?.updated_at || '';

  const reviewTimestamp = (review) => {
    const value = new Date(rawReviewDate(review)).getTime();
    return Number.isNaN(value) ? 0 : value;
  };

  const reviewDate = (review) => {
    const raw = rawReviewDate(review);
    if (!raw) return '';
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
  };

  const ratingStars = (score, suffix = '') => {
    const safeScore = Math.max(0, Math.min(5, numeric(score)));
    const star = element('span', `cr-yotpo-stars${suffix}`);
    star.setAttribute('role', 'img');
    star.setAttribute('aria-label', `${averageLabel(safeScore)} out of 5 stars`);
    const base = element('span', 'cr-yotpo-stars__base', '☆☆☆☆☆');
    base.setAttribute('aria-hidden', 'true');
    const fill = element('span', 'cr-yotpo-stars__fill', '★★★★★');
    fill.setAttribute('aria-hidden', 'true');
    fill.style.width = `${(safeScore / 5) * 100}%`;
    star.append(base, fill);
    return star;
  };

  const emptyStars = () => {
    const star = element('span', 'cr-yotpo-stars cr-yotpo-stars--empty');
    star.setAttribute('role', 'img');
    star.setAttribute('aria-label', 'No rating yet');
    const base = element('span', 'cr-yotpo-stars__base', '☆☆☆☆☆');
    base.setAttribute('aria-hidden', 'true');
    star.append(base);
    return star;
  };

  const replaceChildren = (parent, children) => {
    if (!parent) return;
    parent.replaceChildren(...children);
  };

  const normalizeReview = (review, source) => ({
    raw: review,
    source,
    id: safeText(review?.id || review?.review_id),
    score: Math.max(0, Math.min(5, numeric(review?.score || review?.rating))),
    title: safeText(review?.title).trim(),
    content: safeText(review?.content || review?.review_content || review?.body).trim(),
    reviewer: reviewerName(review),
    date: reviewDate(review),
    verified: isVerified(review),
    incentivized: isIncentivized(review),
    media: imageUrls(review),
    reply: merchantReply(review),
    helpful: Number.isFinite(Number(review?.votes_up ?? review?.helpful_count ?? review?.votes_up_count))
      ? Math.max(0, Number(review?.votes_up ?? review?.helpful_count ?? review?.votes_up_count))
      : null
  });

  const sourceRecords = (root) => {
    const registered = [...root.querySelectorAll('[data-cr-yotpo-source]')].map((node) => ({
      id: safeText(node.dataset.productId).trim(),
      title: safeText(node.dataset.productTitle).trim()
    })).filter((source) => source.id);
    const rootId = safeText(root.dataset.productId).trim();
    const rootTitle = safeText(root.dataset.productTitle).trim();
    if (rootId && !registered.some((source) => source.id === rootId)) registered.unshift({ id: rootId, title: rootTitle });
    return registered.filter((source, index, list) => list.findIndex((item) => item.id === source.id) === index);
  };

  const reviewImageOverrides = (root) => {
    const overrides = new Map();
    [...root.querySelectorAll('[data-cr-yotpo-review-image-override]')].forEach((node) => {
      const reviewId = safeText(node.dataset.reviewId).trim();
      const image = safeUrl(node.dataset.image || '');
      if (reviewId && image) overrides.set(reviewId, { image, alt: safeText(node.dataset.alt).trim() });
    });
    return overrides;
  };

  const feedFor = (source) => {
    const existing = feedCache.get(source.id);
    if (existing) return existing;
    const feed = { source, page: 0, total: null, reviews: [], pending: null, error: '', exhausted: false };
    feedCache.set(source.id, feed);
    return feed;
  };

  const loadNextPage = async (source) => {
    const feed = feedFor(source);
    if (feed.pending) return feed.pending;
    if (feed.exhausted || feed.page >= MAX_PAGES_PER_PRODUCT) return feed;
    feed.pending = requestPage(source.id, feed.page + 1).then((payload) => {
      const pagination = payload.pagination || {};
      const incoming = payload.reviews.map((review) => normalizeReview(review, source));
      feed.page += 1;
      feed.total = Number.isFinite(Number(pagination.total)) ? Number(pagination.total) : feed.total;
      const seen = new Set(feed.reviews.map((review) => review.id).filter(Boolean));
      incoming.forEach((review) => {
        if (!review.id || !seen.has(review.id)) {
          feed.reviews.push(review);
          if (review.id) seen.add(review.id);
        }
      });
      const perPage = numeric(pagination.per_page, REQUEST_SIZE);
      feed.exhausted = feed.page >= MAX_PAGES_PER_PRODUCT || incoming.length === 0 || incoming.length < perPage || (feed.total !== null && feed.reviews.length >= feed.total);
      feed.error = '';
      return feed;
    }).catch((error) => {
      feed.error = error instanceof Error ? error.message : 'Reviews could not be loaded.';
      feed.exhausted = true;
      return feed;
    }).finally(() => { feed.pending = null; });
    return feed.pending;
  };

  const heroContent = (root, bottomline) => {
    const holder = root.querySelector('[data-cr-yotpo-hero-content]');
    if (!holder) return;
    const total = Math.max(0, numeric(bottomline?.total_review));
    const average = Math.max(0, numeric(bottomline?.average_score));
    if (total > 0 && average > 0) {
      if (root.hasAttribute('data-cr-yotpo-hide-empty')) root.hidden = false;
      revealReviewNavigation();
      const link = element('a', 'cr-yotpo-hero__link');
      link.href = '#CrYotpoReviews';
      link.append(ratingStars(average));
      link.append(element('span', 'cr-yotpo-hero__label', `${averageLabel(average)} · ${plural(total, 'review', 'reviews')}`));
      replaceChildren(holder, [link]);
      return;
    }
    if (root.hasAttribute('data-cr-yotpo-hide-empty')) { root.hidden = true; return; }
    const pending = element('span', 'cr-yotpo-hero__link');
    pending.append(emptyStars());
    pending.append(element('span', 'cr-yotpo-hero__label', 'Reviews coming soon'));
    replaceChildren(holder, [pending]);
  };

  const initHero = (root) => {
    if (!root || root.dataset.crYotpoInit === 'true') return;
    root.dataset.crYotpoInit = 'true';
    const productId = safeText(root.dataset.productId).trim();
    if (!productId) return;
    requestPage(productId, 1, 1).then((payload) => heroContent(root, payload.bottomline)).catch(() => heroContent(root, null));
  };

  const featuredEmpty = (root, error) => {
    const state = root.querySelector('[data-cr-yotpo-featured-state]');
    const area = root.querySelector('[data-cr-yotpo-featured-area]');
    if (!area) return;
    const panel = element('div', error ? 'cr-yotpo-featured__empty' : 'cr-yotpo-featured__empty');
    panel.setAttribute('role', error ? 'alert' : 'status');
    panel.append(element('h3', '', error ? 'Reviews are temporarily unavailable' : 'Reviews preview'));
    panel.append(element('p', '', error
      ? 'Published reviews could not be reached right now. Please try again later.'
      : 'Yotpo is connected. Published customer reviews will fill these cards when they are available.'));
    replaceChildren(area, [panel]);
    if (state) state.textContent = error ? 'Reviews are temporarily unavailable.' : 'No published reviews are available yet.';
  };

  const overrideMap = (root) => {
    const map = new Map();
    root.querySelectorAll('[data-cr-review-override]').forEach((node) => {
      const reviewId = safeText(node.dataset.reviewId).trim();
      const image = safeUrl(node.dataset.image || '');
      if (reviewId && image) map.set(reviewId, { image, alt: safeText(node.dataset.alt).trim() });
    });
    return map;
  };

  const placeholderImages = (root) => [...root.querySelectorAll('[data-cr-featured-placeholder]')]
    .map((node) => ({
      slot: numeric(node.dataset.placeholderSlot),
      image: safeUrl(node.dataset.image || ''),
      alt: safeText(node.dataset.alt).trim()
    }))
    .filter((placeholder) => placeholder.image)
    .sort((a, b) => a.slot - b.slot);

  const newestFirst = (a, b) => reviewTimestamp(b.raw) - reviewTimestamp(a.raw)
    || safeText(b.id).localeCompare(safeText(a.id));

  const featuredReviewStatus = (review) => review.verified ? 'Verified customer' : 'Yotpo review';

  const featuredDialog = (root) => {
    const dialog = element('dialog', 'cr-yotpo-featured-dialog');
    const panel = element('div', 'cr-yotpo-featured-dialog__panel');
    const close = element('button', 'cr-yotpo-featured-dialog__close', '×');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close full review');
    const heading = element('h3', 'cr-yotpo-featured-dialog__heading', 'Full review');
    const headingId = `CrYotpoFeaturedDialogHeading-${safeText(root.id || root.dataset.productId || 'reviews').replace(/[^a-z0-9_-]/gi, '-')}`;
    heading.id = headingId;
    dialog.setAttribute('aria-labelledby', headingId);
    const content = element('div', 'cr-yotpo-featured-dialog__content');
    panel.append(close, heading, content);
    dialog.append(panel);
    root.append(dialog);
    let returnFocus = null;
    const closeDialog = () => {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    };
    const open = (review, trigger) => {
      returnFocus = trigger;
      const quote = element('blockquote', 'cr-yotpo-featured-dialog__quote', review.content);
      const meta = element('div', 'cr-yotpo-featured-dialog__meta');
      if (review.reviewer) meta.append(element('strong', 'cr-yotpo-featured-dialog__reviewer', review.reviewer));
      meta.append(element('span', 'cr-yotpo-featured-dialog__verified', featuredReviewStatus(review)));
      replaceChildren(content, [ratingStars(review.score, ' cr-yotpo-stars--dialog'), quote, meta]);
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      close.focus();
    };
    close.addEventListener('click', closeDialog);
    dialog.addEventListener('click', (event) => { if (event.target === dialog) closeDialog(); });
    dialog.addEventListener('close', () => returnFocus?.focus());
    return { dialog, open };
  };

  const featuredCard = (review, overrides, placeholders, index, total, openFullReview) => {
    const card = element('article', 'cr-yotpo-featured__card');
    card.setAttribute('role', 'group');
    card.setAttribute('aria-roledescription', 'slide');
    card.setAttribute('aria-label', `Review ${index + 1} of ${total}`);
    const override = overrides.get(review.id);
    const reviewMedia = review.media[0];
    const placeholder = placeholders[index % placeholders.length];
    const image = override?.image || reviewMedia || placeholder?.image;
    const isPlaceholder = !override?.image && !reviewMedia;
    const figure = element('figure', `cr-yotpo-featured__image${isPlaceholder ? ' cr-yotpo-featured__image--placeholder' : ''}`);
    if (image) {
      const img = element('img');
      img.src = image;
      img.loading = 'lazy';
      img.alt = override?.alt || (isPlaceholder ? placeholder?.alt : 'Customer review photo') || 'Product image';
      figure.append(img);
    }
    if (isPlaceholder) figure.append(element('span', 'cr-yotpo-featured__photo-badge', 'PHOTO COMING SOON'));
    card.append(figure);
    const body = element('div', 'cr-yotpo-featured__body');
    body.append(ratingStars(review.score));
    if (review.content) {
      const quote = element('blockquote', 'cr-yotpo-featured__quote', review.content);
      const more = element('button', 'cr-yotpo-featured__read-more', 'Read more');
      more.type = 'button';
      more.setAttribute('aria-label', `Read full review by ${review.reviewer || 'customer'}`);
      more.addEventListener('click', () => openFullReview(review, more));
      body.append(quote, more);
    }
    const meta = element('div', 'cr-yotpo-featured__meta');
    if (review.reviewer) meta.append(element('strong', 'cr-yotpo-featured__reviewer', review.reviewer));
    meta.append(element('span', 'cr-yotpo-featured__verified', featuredReviewStatus(review)));
    if (meta.childNodes.length) body.append(meta);
    card.append(body);
    return card;
  };

  const initFeatured = (root) => {
    if (!root || root.dataset.crYotpoInit === 'true') return;
    root.dataset.crYotpoInit = 'true';
    const source = sourceRecords(root)[0];
    const state = root.querySelector('[data-cr-yotpo-featured-state]');
    const area = root.querySelector('[data-cr-yotpo-featured-area]');
    if (!source || !area) return;

    requestPage(source.id, 1, REQUEST_SIZE).then((payload) => {
      const reviews = payload.reviews
        .map((review) => normalizeReview(review, source))
        .filter((review) => review.score === 5)
        .sort(newestFirst)
        .slice(0, 6);
      if (!reviews.length) {
        featuredEmpty(root, false);
        return;
      }
      revealReviewNavigation();
      const overrides = overrideMap(root);
      const placeholders = placeholderImages(root);
      const fullReview = featuredDialog(root);
      const viewport = element('div', 'cr-yotpo-featured__viewport');
      viewport.tabIndex = 0;
      viewport.setAttribute('role', 'region');
      viewport.setAttribute('aria-roledescription', 'carousel');
      viewport.setAttribute('aria-label', root.dataset.carouselLabel || 'Customer reviews');
      const track = element('div', 'cr-yotpo-featured__track');
      [0, 1, 2].forEach((setIndex) => reviews.forEach((review, index) => {
        const card = featuredCard(review, overrides, placeholders, index, reviews.length, fullReview.open);
        card.dataset.reviewIndex = String(index);
        card.dataset.carouselSet = String(setIndex);
        if (setIndex !== 1) {
          card.setAttribute('aria-hidden', 'true');
          card.inert = true;
          card.querySelectorAll('button, a').forEach((control) => control.tabIndex = -1);
        }
        track.append(card);
      }));
      viewport.append(track);
      const controls = element('div', 'cr-yotpo-featured__controls');
      const previous = element('button', 'cr-yotpo-control cr-yotpo-control--icon', '←');
      previous.type = 'button';
      previous.setAttribute('aria-label', 'Show previous review');
      const next = element('button', 'cr-yotpo-control cr-yotpo-control--icon', '→');
      next.type = 'button';
      next.setAttribute('aria-label', 'Show next review');
      const pause = element('button', 'cr-yotpo-control', 'Pause');
      pause.type = 'button';
      pause.setAttribute('aria-label', 'Pause automatic review rotation');
      const counter = element('span', 'cr-yotpo-featured__counter', `1 of ${reviews.length}`);
      counter.setAttribute('aria-live', 'polite');
      const pagination = element('div', 'cr-yotpo-featured__pagination');
      pagination.setAttribute('aria-label', 'Review carousel pagination');
      const dots = reviews.map((review, index) => {
        const dot = element('button', 'cr-yotpo-featured__dot');
        dot.type = 'button';
        dot.setAttribute('aria-label', `Show review ${index + 1} of ${reviews.length}`);
        if (index === 0) dot.setAttribute('aria-current', 'true');
        pagination.append(dot);
        return dot;
      });
      controls.append(previous, next, pause, counter);
      replaceChildren(area, [viewport, controls, pagination]);
      if (state) state.textContent = `${reviews.length} published ${plural(reviews.length, 'review', 'reviews')} are available in the carousel.`;

      const mobileCarousel = window.matchMedia('(max-width: 760px)');
      // Mobile is intentionally manual-first: after a person swipes a review card, a timer must
      // never pull that card away. Desktop keeps the lightweight automatic rotation until the
      // shopper uses a carousel control. In either context, Play is the explicit opt-in to resume.
      let pausedByUser = reduceMotion.matches || mobileCarousel.matches;
      let pausedByInteraction = false;
      let active = 0;
      let timer = null;
      const cards = [...track.children];
      const logicalCards = cards.filter((card) => card.dataset.carouselSet === '1');
      let activeCard = logicalCards[0] || cards[0];
      let scrollFrame = null;
      let scrollEndTimer = null;
      const cardCenter = (card) => card.offsetLeft + (card.offsetWidth / 2);
      const centeredLeft = (card) => cardCenter(card) - (track.clientWidth / 2);
      const nearestCard = () => {
        const center = track.scrollLeft + (track.clientWidth / 2);
        return cards.reduce((nearest, card) => (
          !nearest || Math.abs(cardCenter(card) - center) < Math.abs(cardCenter(nearest) - center) ? card : nearest
        ), null);
      };
      const updateActive = (requestedCard) => {
        activeCard = requestedCard || nearestCard() || activeCard;
        active = Number(activeCard?.dataset.reviewIndex || 0);
        cards.forEach((card) => card.classList.toggle('is-active', card === activeCard));
        counter.textContent = `${active + 1} of ${reviews.length}`;
        previous.disabled = false;
        next.disabled = false;
        dots.forEach((dot, index) => {
          dot.classList.toggle('is-active', index === active);
          if (index === active) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      };
      const centerCard = (card, behavior = reduceMotion.matches ? 'auto' : 'smooth') => {
        if (!card) return;
        track.scrollTo({
          left: centeredLeft(card),
          behavior
        });
        updateActive(card);
      };
      const normalizeToMiddle = () => {
        const current = nearestCard();
        if (!current) return;
        const middle = logicalCards[Number(current.dataset.reviewIndex || 0)];
        if (middle && current.dataset.carouselSet !== '1') {
          track.scrollTo({
            left: centeredLeft(middle),
            behavior: 'auto'
          });
          updateActive(middle);
        } else {
          updateActive(current);
        }
      };
      const queueNormalize = () => {
        if (scrollEndTimer) window.clearTimeout(scrollEndTimer);
        scrollEndTimer = window.setTimeout(normalizeToMiddle, 180);
      };
      const moveTo = (destination) => {
        const logicalIndex = ((destination % reviews.length) + reviews.length) % reviews.length;
        const center = track.scrollLeft + (track.clientWidth / 2);
        const candidates = cards.filter((card) => Number(card.dataset.reviewIndex) === logicalIndex);
        const target = candidates.reduce((nearest, card) => (
          !nearest || Math.abs(cardCenter(card) - center) < Math.abs(cardCenter(nearest) - center) ? card : nearest
        ), null) || logicalCards[logicalIndex];
        centerCard(target);
        queueNormalize();
      };
      const move = (direction) => {
        const current = nearestCard() || activeCard;
        const position = cards.indexOf(current);
        let target = cards[position + direction];
        if (!target) target = direction > 0 ? cards[reviews.length] : cards[(reviews.length * 2) - 1];
        centerCard(target);
        queueNormalize();
      };
      const stopTimer = () => { if (timer) window.clearInterval(timer); timer = null; };
      const updatePauseControl = () => {
        pause.textContent = pausedByUser ? 'Play' : 'Pause';
        pause.setAttribute('aria-label', pausedByUser ? 'Start automatic review rotation' : 'Pause automatic review rotation');
        pause.setAttribute('aria-pressed', pausedByUser ? 'true' : 'false');
      };
      const startTimer = () => {
        stopTimer();
        if (pausedByUser || pausedByInteraction || reduceMotion.matches || reviews.length < 2) return;
        timer = window.setInterval(() => {
          if (document.hidden || fullReview.dialog.open) return;
          move(1);
        }, 5500);
      };
      const interactionPause = () => { pausedByInteraction = true; stopTimer(); };
      const interactionResume = () => { pausedByInteraction = false; startTimer(); };
      const pauseForManualNavigation = () => {
        pausedByUser = true;
        pausedByInteraction = false;
        stopTimer();
        updatePauseControl();
      };
      previous.addEventListener('click', () => { pauseForManualNavigation(); move(-1); });
      next.addEventListener('click', () => { pauseForManualNavigation(); move(1); });
      dots.forEach((dot, index) => dot.addEventListener('click', () => { pauseForManualNavigation(); moveTo(index); }));
      pause.addEventListener('click', () => {
        pausedByUser = !pausedByUser;
        updatePauseControl();
        startTimer();
      });
      viewport.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') { event.preventDefault(); pauseForManualNavigation(); move(1); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); pauseForManualNavigation(); move(-1); }
        if (event.key === 'Home') { event.preventDefault(); pauseForManualNavigation(); moveTo(0); }
        if (event.key === 'End') { event.preventDefault(); pauseForManualNavigation(); moveTo(cards.length - 1); }
      });
      viewport.addEventListener('mouseenter', interactionPause);
      viewport.addEventListener('mouseleave', interactionResume);
      viewport.addEventListener('focusin', interactionPause);
      viewport.addEventListener('focusout', (event) => { if (!viewport.contains(event.relatedTarget)) interactionResume(); });
      // Use touchstart as well as pointerdown for Safari versions that do not expose touch pointers.
      // Scrolling is not the signal because script-driven carousel centering also scrolls the track.
      const pauseForTouch = () => pauseForManualNavigation();
      track.addEventListener('pointerdown', (event) => { if (event.pointerType === 'touch' || event.pointerType === 'pen') pauseForTouch(); }, { passive: true });
      track.addEventListener('touchstart', pauseForTouch, { passive: true });
      track.addEventListener('scroll', () => {
        if (!scrollFrame) scrollFrame = window.requestAnimationFrame(() => { scrollFrame = null; updateActive(); });
        queueNormalize();
      }, { passive: true });
      track.addEventListener('scrollend', normalizeToMiddle);
      reduceMotion.addEventListener?.('change', (event) => { if (event.matches) pausedByUser = true; updatePauseControl(); startTimer(); });
      window.requestAnimationFrame(() => centerCard(activeCard, 'auto'));
      updatePauseControl();
      startTimer();
    }).catch(() => featuredEmpty(root, true));
  };

  const reviewCard = (review, allProducts, imageOverrides) => {
    const article = element('article', 'cr-yotpo-review');
    const main = element('div', 'cr-yotpo-review__main');
    const top = element('div', 'cr-yotpo-review__top');
    top.append(ratingStars(review.score));
    top.append(element('span', 'cr-yotpo-review__score', `${averageLabel(review.score)} / 5`));
    if (review.date) top.append(element('time', 'cr-yotpo-review__date', review.date));
    main.append(top);
    if (review.title) main.append(element('h3', 'cr-yotpo-review__title', review.title));
    if (review.content) main.append(element('p', 'cr-yotpo-review__content', review.content));
    const meta = element('div', 'cr-yotpo-review__meta');
    meta.append(element('span', '', review.reviewer));
    if (review.verified) meta.append(element('span', 'cr-yotpo-review__badge', 'Verified buyer'));
    if (review.incentivized) meta.append(element('span', 'cr-yotpo-review__badge cr-yotpo-review__badge--disclosure', 'Incentivized review'));
    if (allProducts && review.source.title) meta.append(element('span', 'cr-yotpo-review__product', review.source.title));
    if (review.helpful !== null) meta.append(element('span', 'cr-yotpo-review__helpful', `${plural(review.helpful, 'person', 'people')} found this helpful`));
    main.append(meta);
    const override = imageOverrides?.get(review.id);
    const mediaItems = review.media.map((url, index) => ({ url, alt: `Review photo ${index + 1}` }));
    if (override && !mediaItems.some((item) => item.url === override.image)) mediaItems.push({ url: override.image, alt: override.alt || 'Customer review photo' });
    if (mediaItems.length) {
      const media = element('div', 'cr-yotpo-review__media');
      mediaItems.forEach((item) => {
        const image = element('img');
        image.src = item.url;
        image.loading = 'lazy';
        image.alt = item.alt;
        media.append(image);
      });
      main.append(media);
    }
    if (review.reply) {
      const reply = element('aside', 'cr-yotpo-review__reply');
      reply.append(element('strong', '', 'Merchant reply'));
      reply.append(element('span', '', review.reply));
      main.append(reply);
    }
    article.append(main);
    return article;
  };

  const reviewSkeletons = () => Array.from({ length: 3 }, () => element('div', 'cr-yotpo-review__skeleton'));

  const initFullReviews = (root) => {
    if (!root || root.dataset.crYotpoInit === 'true') return;
    root.dataset.crYotpoInit = 'true';
    const sources = sourceRecords(root);
    const imageOverrides = reviewImageOverrides(root);
    const allProducts = root.dataset.scope === 'all';
    const form = root.querySelector('[data-cr-yotpo-filters]');
    const results = root.querySelector('[data-cr-yotpo-results]');
    const status = root.querySelector('[data-cr-yotpo-status]');
    const loadMore = root.querySelector('[data-cr-yotpo-load-more]');
    const clear = root.querySelector('[data-cr-yotpo-clear]');
    const empty = root.querySelector('[data-cr-yotpo-empty]');
    const errorBox = root.querySelector('[data-cr-yotpo-error]');
    const ratingMenu = form?.querySelector('[data-cr-yotpo-rating-menu]');
    const ratingSummary = form?.querySelector('[data-cr-yotpo-rating-summary]');
    const ratingCounts = [...form.querySelectorAll('[data-cr-yotpo-rating-count]')];
    if (!sources.length || !form || !results) return;
    let visible = VISIBLE_PAGE_SIZE;
    let loading = false;

    const controls = {
      ratings: () => [...form.querySelectorAll('input[name="cr-yotpo-rating"]:checked')]
        .map((input) => numeric(input.value))
        .filter((rating) => rating >= 1 && rating <= 5),
      media: () => Boolean(form.querySelector('[name="media"]')?.checked),
      sort: () => form.querySelector('[name="sort"]')?.value || 'newest',
      query: () => safeText(form.querySelector('[name="query"]')?.value).trim().toLocaleLowerCase()
    };

    const loadedReviews = () => sources.flatMap((source) => feedFor(source).reviews);
    const filteredReviews = () => {
      const filter = controls;
      let reviews = loadedReviews().filter((review) => {
        const ratings = filter.ratings();
        if (ratings.length && !ratings.includes(Math.round(review.score))) return false;
        if (filter.media() && !review.media.length) return false;
        if (filter.query()) {
          const haystack = `${review.title} ${review.content} ${review.reviewer} ${review.source.title}`.toLocaleLowerCase();
          if (!haystack.includes(filter.query())) return false;
        }
        return true;
      });
      const toTime = (review) => {
        const raw = review.raw?.created_at || review.raw?.created_at_date || review.raw?.date || review.raw?.updated_at;
        const value = new Date(raw || 0).getTime();
        return Number.isNaN(value) ? 0 : value;
      };
      const helpful = (review) => review.helpful === null ? 0 : review.helpful;
      reviews = reviews.sort((a, b) => {
        const sort = filter.sort();
        if (sort === 'highest') return b.score - a.score || toTime(b) - toTime(a);
        if (sort === 'lowest') return a.score - b.score || toTime(b) - toTime(a);
        if (sort === 'helpful') return helpful(b) - helpful(a) || toTime(b) - toTime(a);
        return toTime(b) - toTime(a);
      });
      return reviews;
    };

    const updateRatingSummary = () => {
      if (!ratingSummary) return;
      const ratings = controls.ratings();
      if (!ratings.length) ratingSummary.textContent = 'All ratings';
      else if (ratings.length === 1) ratingSummary.textContent = `${ratings[0]} ${ratings[0] === 1 ? 'star' : 'stars'}`;
      else ratingSummary.textContent = `${ratings.length} ratings`;
    };

    const updateRatingCounts = () => {
      const counts = new Map([[1, 0], [2, 0], [3, 0], [4, 0], [5, 0]]);
      loadedReviews().forEach((review) => {
        const rating = Math.round(review.score);
        if (counts.has(rating)) counts.set(rating, counts.get(rating) + 1);
      });
      ratingCounts.forEach((node) => {
        const rating = numeric(node.dataset.rating);
        const count = counts.get(rating) || 0;
        node.textContent = `(${count})`;
        const accessibleLabel = node.closest('.cr-yotpo__rating-option')?.querySelector('[data-cr-yotpo-rating-label]');
        if (accessibleLabel) accessibleLabel.textContent = `${rating} ${rating === 1 ? 'star' : 'stars'}, ${plural(count, 'review', 'reviews')}`;
      });
    };

    const mayFetchMore = () => sources.some((source) => {
      const feed = feedFor(source);
      return !feed.exhausted && feed.page < MAX_PAGES_PER_PRODUCT;
    });

    const render = () => {
      const reviews = filteredReviews();
      updateRatingCounts();
      replaceChildren(results, reviews.slice(0, visible).map((review) => reviewCard(review, allProducts, imageOverrides)));
      results.setAttribute('aria-busy', loading ? 'true' : 'false');
      const errors = sources.map((source) => feedFor(source).error).filter(Boolean);
      setHidden(errorBox, !errors.length);
      if (errorBox && errors.length) errorBox.querySelector('[data-cr-yotpo-error-copy]').textContent = 'Some published reviews could not be loaded. You can try again later.';
      const totalLoaded = loadedReviews().length;
      if (root.hasAttribute('data-cr-yotpo-hide-empty')) root.hidden = totalLoaded === 0;
      if (totalLoaded > 0) revealReviewNavigation();
      if (status) status.textContent = reviews.length
        ? `${plural(reviews.length, 'published review', 'published reviews')} match the current filters.`
        : totalLoaded ? 'No loaded reviews match the current filters.' : 'No published reviews are available yet.';
      setHidden(empty, Boolean(reviews.length) || loading);
      const remainingLocal = reviews.length > visible;
      const canLoadMore = remainingLocal || mayFetchMore();
      setHidden(loadMore, !canLoadMore);
      if (loadMore) {
        loadMore.disabled = loading;
        loadMore.textContent = loading ? 'Loading reviews…' : remainingLocal ? 'Load more reviews' : 'Load more from Yotpo';
      }
    };

    const fetchInitial = async () => {
      loading = true;
      if (status) status.textContent = 'Loading published reviews…';
      replaceChildren(results, reviewSkeletons());
      await Promise.all(sources.map((source) => loadNextPage(source)));
      loading = false;
      render();
    };

    const fetchMore = async () => {
      const reviews = filteredReviews();
      if (reviews.length > visible) {
        visible += VISIBLE_PAGE_SIZE;
        render();
        return;
      }
      if (!mayFetchMore() || loading) return;
      loading = true;
      render();
      await Promise.all(sources.filter((source) => {
        const feed = feedFor(source);
        return !feed.exhausted && feed.page < MAX_PAGES_PER_PRODUCT;
      }).map((source) => loadNextPage(source)));
      visible += VISIBLE_PAGE_SIZE;
      loading = false;
      render();
    };

    form.addEventListener('input', () => { visible = VISIBLE_PAGE_SIZE; render(); });
    form.addEventListener('change', () => { visible = VISIBLE_PAGE_SIZE; updateRatingSummary(); render(); });
    clear?.addEventListener('click', () => {
      form.reset();
      visible = VISIBLE_PAGE_SIZE;
      updateRatingSummary();
      ratingMenu?.removeAttribute('open');
      render();
      form.querySelector('[name="query"]')?.focus();
    });
    ratingMenu?.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      ratingMenu.removeAttribute('open');
      ratingMenu.querySelector('summary')?.focus();
    });
    document.addEventListener('click', (event) => {
      if (ratingMenu?.open && !ratingMenu.contains(event.target)) ratingMenu.removeAttribute('open');
    });
    loadMore?.addEventListener('click', fetchMore);
    updateRatingSummary();
    fetchInitial();
  };

  const initAll = (scope = document) => {
    const roots = [];
    if (scope instanceof Element && scope.matches('[data-cr-yotpo-hero], [data-cr-yotpo-featured], [data-cr-yotpo-reviews]')) roots.push(scope);
    if (scope.querySelectorAll) roots.push(...scope.querySelectorAll('[data-cr-yotpo-hero], [data-cr-yotpo-featured], [data-cr-yotpo-reviews]'));
    roots.forEach((root) => {
      if (root.matches('[data-cr-yotpo-hero]')) initHero(root);
      if (root.matches('[data-cr-yotpo-featured]')) initFeatured(root);
      if (root.matches('[data-cr-yotpo-reviews]')) initFullReviews(root);
    });
  };

  window.CappyRootsYotpoReviews = { initAll, endpointFor, STORE_ID };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  else initAll(document);
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
